import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Banner, Field } from '../components/ui';
import { BrandLockup } from '../components/Brand';

export function ResetPasswordPage() {
  const { setUser, setCommunity } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const firstError = (key: string) => fieldErrors[key]?.[0];

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    if (password !== confirm) {
      setFieldErrors({ password: ['The two passwords do not match.'] });
      return;
    }

    setBusy(true);
    try {
      const { user, community } = await api.resetPassword(token, password);
      setUser(user);
      setCommunity(community);
      navigate('/', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
        setFieldErrors(caught.details ?? {});
      } else {
        setError('We could not reach the server. Check your connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <BrandLockup size="sm" />
        </div>
        <h1 className="page-title">Choose a new password</h1>
        <p className="page-lede">Use the link from your email. It expires after the time mentioned there.</p>

        <div className="card">
          {!token ? (
            <Banner kind="error">
              This reset link is missing or incomplete.{' '}
              <Link to="/">Request a new one from the sign-in page.</Link>
            </Banner>
          ) : (
            <>
              {error && <Banner kind="error">{error}</Banner>}
              <form onSubmit={submit} noValidate>
                <Field label="New password" error={firstError('password')} hint="At least 8 characters.">
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="new-password"
                    required
                    minLength={8}
                  />
                </Field>
                <Field label="Confirm password">
                  <input
                    type="password"
                    value={confirm}
                    onChange={(event) => setConfirm(event.target.value)}
                    autoComplete="new-password"
                    required
                    minLength={8}
                  />
                </Field>
                <button type="submit" className="btn block" disabled={busy} style={{ marginTop: 8 }}>
                  {busy ? 'Saving…' : 'Save password and sign in'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
